/**
 * Enhanced image caching utility with intelligent preloading and bandwidth awareness
 * Uses in-memory cache with browser Cache API fallback
 */

import { getNetworkQuality, startImageLoadTimer, endImageLoadTimer, recordUserExperience } from './performanceMonitor';

// Enhanced cache configuration
const CACHE_NAME = 'fortress-images-v3';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes in milliseconds

// Dynamic cache limits based on device capabilities
const getMaxCacheEntries = () => {
  const memory = navigator.deviceMemory || 4; // Default to 4GB if not available
  const networkQuality = getNetworkQuality();
  
  // Adjust cache size based on device memory and network quality
  let baseSize = 50;
  if (memory >= 8) baseSize = 100;
  else if (memory >= 4) baseSize = 75;
  else if (memory <= 2) baseSize = 25;
  
  // Reduce cache size on poor networks to save bandwidth
  if (networkQuality === 'poor' || networkQuality === 'slow') {
    baseSize = Math.floor(baseSize * 0.6);
  }
  
  return Math.max(baseSize, 10); // Minimum 10 entries
};

// In-memory cache for quick access
const memoryCache = new Map();

// Preload queue and management
const preloadQueue = new Set();
const preloadInProgress = new Set();
const MAX_CONCURRENT_PRELOADS = 3;

// Simple metadata structure
class CacheEntry {
  constructor(url, blob, contentType) {
    this.url = url;
    this.blob = blob;
    this.contentType = contentType;
    this.timestamp = Date.now();
    this.objectUrl = URL.createObjectURL(blob);
  }

  isExpired() {
    return Date.now() - this.timestamp > CACHE_EXPIRY;
  }

  cleanup() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}

/**
 * Check if browser supports basic caching
 */
const isCacheSupported = () => {
  return 'caches' in window;
};

/**
 * Clean up expired entries from memory cache with intelligent memory management
 */
const cleanupMemoryCache = () => {
  const now = Date.now();
  const maxEntries = getMaxCacheEntries();
  const toDelete = [];
  
  for (const [key, entry] of memoryCache.entries()) {
    if (entry.isExpired()) {
      entry.cleanup();
      toDelete.push(key);
    }
  }
  
  toDelete.forEach(key => memoryCache.delete(key));
  
  // If still too many entries, remove oldest ones using LRU strategy
  if (memoryCache.size > maxEntries) {
    const entries = Array.from(memoryCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = entries.slice(0, memoryCache.size - maxEntries);
    toRemove.forEach(([key, entry]) => {
      entry.cleanup();
      memoryCache.delete(key);
    });
    
    console.log(`Cache cleanup: removed ${toRemove.length} old entries, ${memoryCache.size}/${maxEntries} remaining`);
  }
};

/**
 * Get bandwidth-aware timeout for operations
 */
const getBandwidthAwareTimeout = (baseTimeout = 10000) => {
  const networkQuality = getNetworkQuality();
  
  switch (networkQuality) {
    case 'good':
      return baseTimeout;
    case 'slow':
      return baseTimeout * 2;
    case 'poor':
      return baseTimeout * 3;
    case 'offline':
      return 1000; // Very short timeout for offline
    default:
      return baseTimeout * 1.5;
  }
};

/**
 * Get optimal batch size based on network conditions
 */
const getOptimalBatchSize = () => {
  const networkQuality = getNetworkQuality();
  const memory = navigator.deviceMemory || 4;
  
  let batchSize = 2; // Conservative default
  
  if (networkQuality === 'good' && memory >= 4) {
    batchSize = 4;
  } else if (networkQuality === 'good') {
    batchSize = 3;
  } else if (networkQuality === 'slow') {
    batchSize = 2;
  } else if (networkQuality === 'poor') {
    batchSize = 1;
  }
  
  return Math.min(batchSize, MAX_CONCURRENT_PRELOADS);
};

/**
 * Get image from cache (simplified)
 */
export const getCachedImage = async (url, options = {}) => {
  if (!url) return null;
  
  try {
    // Clean up expired entries first
    cleanupMemoryCache();
    
    // Check memory cache first
    const cacheKey = url; // Simplified - no complex key generation
    const entry = memoryCache.get(cacheKey);
    
    if (entry && !entry.isExpired()) {
      return {
        url: entry.objectUrl,
        cached: true,
        source: 'memory'
      };
    }
    
    // If expired, clean it up
    if (entry) {
      entry.cleanup();
      memoryCache.delete(cacheKey);
    }
    
    return null;
  } catch (error) {
    console.warn('Cache lookup failed (non-critical):', error);
    return null;
  }
};

/**
 * Cache an image with bandwidth-aware optimization
 */
export const cacheImage = async (url, options = {}) => {
  if (!url || !isCacheSupported()) return false;
  
  const timerId = startImageLoadTimer(`cache_${url}`, {
    operationType: 'cache',
    imageUrl: url,
    networkQuality: getNetworkQuality()
  });
  
  try {
    // Clean up memory cache first
    cleanupMemoryCache();
    
    // Skip if already cached
    if (memoryCache.has(url)) {
      endImageLoadTimer(timerId, { success: true, cached: true });
      return true;
    }
    
    // Use bandwidth-aware timeout
    const timeout = getBandwidthAwareTimeout(10000);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'default',
      // Add priority hint for modern browsers
      priority: options.priority || 'low'
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.warn(`Failed to fetch image for caching: ${response.status} - ${url}`);
      endImageLoadTimer(timerId, { success: false, error: `HTTP ${response.status}` });
      return false;
    }
    
    const blob = await response.blob();
    const entry = new CacheEntry(url, blob, response.headers.get('content-type') || 'image/jpeg');
    
    memoryCache.set(url, entry);
    console.log(`Image cached in memory: ${url} (${(blob.size / 1024).toFixed(1)}KB)`);
    
    endImageLoadTimer(timerId, { 
      success: true, 
      cached: false,
      fileSize: blob.size 
    });
    
    return true;
  } catch (error) {
    endImageLoadTimer(timerId, { success: false, error: error.message });
    
    if (error.name === 'AbortError') {
      console.warn('Image caching timed out:', url);
    } else {
      console.warn('Error caching image (non-critical):', error);
    }
    return false;
  }
};

/**
 * Intelligent preload and cache multiple images with bandwidth awareness
 */
export const preloadImages = async (urls, options = {}) => {
  if (!isCacheSupported() || !urls || urls.length === 0) return [];
  
  const results = [];
  const batchSize = options.batchSize || getOptimalBatchSize();
  const { onProgress, priority = 'low' } = options;
  const networkQuality = getNetworkQuality();
  
  // Skip preloading on poor networks unless explicitly requested
  if (networkQuality === 'poor' && !options.forcePoor) {
    console.log('Skipping preload on poor network connection');
    return urls.map(url => ({ url, success: false, skipped: true, reason: 'poor_network' }));
  }
  
  console.log(`Starting intelligent preload of ${urls.length} images (batch size: ${batchSize}, network: ${networkQuality})`);
  
  // Process in bandwidth-aware batches
  for (let i = 0; i < urls.length; i += batchSize) {
    const batch = urls.slice(i, i + batchSize);
    
    const batchPromises = batch.map(async (url) => {
      // Skip if already in progress or cached
      if (preloadInProgress.has(url) || memoryCache.has(url)) {
        return { url, success: true, cached: true };
      }
      
      preloadInProgress.add(url);
      
      try {
        const success = await cacheImage(url, { priority });
        return { url, success };
      } catch (error) {
        console.warn(`Error preloading ${url}:`, error);
        return { url, success: false, error: error.message };
      } finally {
        preloadInProgress.delete(url);
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    if (onProgress) {
      onProgress(Math.min(i + batchSize, urls.length), urls.length);
    }
    
    // Add delay between batches on slower networks
    if (i + batchSize < urls.length && (networkQuality === 'slow' || networkQuality === 'poor')) {
      await new Promise(resolve => setTimeout(resolve, networkQuality === 'slow' ? 500 : 1000));
    }
  }
  
  const successful = results.filter(r => r.success).length;
  console.log(`Preload completed: ${successful}/${urls.length} images cached successfully`);
  
  return results;
};

/**
 * Intelligent preload based on viewport and user behavior
 */
export const intelligentPreload = async (imageElements, options = {}) => {
  if (!imageElements || imageElements.length === 0) return [];
  
  const { 
    viewportMargin = '50px',
    priorityThreshold = 0.5,
    maxConcurrent = MAX_CONCURRENT_PRELOADS 
  } = options;
  
  // Create intersection observer for viewport detection
  const visibleImages = [];
  const nearViewportImages = [];
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const url = entry.target.dataset.src || entry.target.src;
      if (!url) return;
      
      if (entry.isIntersecting) {
        visibleImages.push(url);
      } else if (entry.intersectionRatio > priorityThreshold) {
        nearViewportImages.push(url);
      }
    });
  }, {
    rootMargin: viewportMargin,
    threshold: [0, priorityThreshold, 1]
  });
  
  // Observe all image elements
  imageElements.forEach(img => observer.observe(img));
  
  // Wait a bit for intersection observer to work
  await new Promise(resolve => setTimeout(resolve, 100));
  
  observer.disconnect();
  
  // Prioritize visible images first, then near-viewport images
  const prioritizedUrls = [...visibleImages, ...nearViewportImages];
  
  if (prioritizedUrls.length === 0) return [];
  
  console.log(`Intelligent preload: ${visibleImages.length} visible, ${nearViewportImages.length} near viewport`);
  
  return await preloadImages(prioritizedUrls, {
    ...options,
    priority: 'high' // Higher priority for intelligent preload
  });
};

/**
 * Remove image from cache (simplified)
 */
export const removeCachedImage = async (url, options = {}) => {
  if (!url) return false;
  
  try {
    const entry = memoryCache.get(url);
    if (entry) {
      entry.cleanup();
      memoryCache.delete(url);
      console.log(`Image removed from cache: ${url}`);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('Error removing cached image:', error);
    return false;
  }
};

/**
 * Clear all cached images (simplified)
 */
export const clearImageCache = async () => {
  try {
    // Clear memory cache
    for (const [key, entry] of memoryCache.entries()) {
      entry.cleanup();
    }
    memoryCache.clear();
    
    // Clear browser cache
    if (isCacheSupported()) {
      await caches.delete(CACHE_NAME);
    }
    
    console.log('Image cache cleared');
    return true;
  } catch (error) {
    console.warn('Error clearing image cache:', error);
    return false;
  }
};

/**
 * Get enhanced cache statistics with performance metrics
 */
export const getCacheStats = async () => {
  try {
    cleanupMemoryCache();
    
    const entries = Array.from(memoryCache.values());
    const totalSize = entries.reduce((sum, entry) => sum + (entry.blob?.size || 0), 0);
    const timestamps = entries.map(entry => entry.timestamp).filter(Boolean);
    const maxEntries = getMaxCacheEntries();
    const networkQuality = getNetworkQuality();
    
    return {
      supported: isCacheSupported(),
      entries: memoryCache.size,
      maxEntries: maxEntries,
      utilizationPercent: Math.round((memoryCache.size / maxEntries) * 100),
      totalSize: totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      averageImageSize: entries.length > 0 ? Math.round(totalSize / entries.length) : 0,
      oldestEntry: timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null,
      newestEntry: timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null,
      cacheExpiry: CACHE_EXPIRY,
      networkQuality: networkQuality,
      deviceMemory: navigator.deviceMemory || 'unknown',
      preloadInProgress: preloadInProgress.size,
      optimalBatchSize: getOptimalBatchSize()
    };
  } catch (error) {
    console.warn('Error getting cache stats:', error);
    return {
      supported: isCacheSupported(),
      entries: 0,
      totalSize: 0,
      error: error.message
    };
  }
};

/**
 * Adaptive image loading with bandwidth and device awareness
 */
export const adaptiveImageLoad = async (url, options = {}) => {
  if (!url) return null;
  
  const networkQuality = getNetworkQuality();
  const deviceMemory = navigator.deviceMemory || 4;
  
  // Determine loading strategy based on conditions
  const strategy = {
    useCache: true,
    preload: false,
    quality: 'auto',
    timeout: getBandwidthAwareTimeout(5000)
  };
  
  // Adjust strategy based on network quality
  if (networkQuality === 'poor') {
    strategy.useCache = false; // Skip cache on poor networks to save bandwidth
    strategy.quality = 'low';
    strategy.timeout = 15000;
  } else if (networkQuality === 'slow') {
    strategy.preload = false;
    strategy.quality = 'medium';
  } else if (networkQuality === 'good' && deviceMemory >= 4) {
    strategy.preload = true;
    strategy.quality = 'high';
  }
  
  // Apply user preferences if provided
  if (options.forceCache !== undefined) strategy.useCache = options.forceCache;
  if (options.quality) strategy.quality = options.quality;
  
  console.log(`Adaptive loading for ${url}:`, strategy);
  
  try {
    if (strategy.useCache) {
      const cached = await getCachedImage(url, options);
      if (cached) {
        recordUserExperience({ 
          loadSource: 'cache',
          networkQuality: networkQuality,
          strategy: strategy
        });
        return cached;
      }
    }
    
    // Background caching for future use
    if (strategy.preload && url.startsWith('http')) {
      cacheImage(url, { priority: 'low' }).catch(error => {
        console.warn('Background caching failed:', error);
      });
    }
    
    recordUserExperience({ 
      loadSource: 'network',
      networkQuality: networkQuality,
      strategy: strategy
    });
    
    return {
      url: url,
      cached: false,
      strategy: strategy
    };
    
  } catch (error) {
    console.warn('Adaptive loading error:', error);
    return {
      url: url,
      cached: false,
      error: error.message,
      strategy: strategy
    };
  }
};

/**
 * Memory-aware cache cleanup with performance optimization
 */
export const optimizeCache = async () => {
  try {
    const stats = await getCacheStats();
    const memoryPressure = stats.utilizationPercent > 80;
    const networkQuality = getNetworkQuality();
    
    console.log(`Cache optimization: ${stats.utilizationPercent}% utilized, network: ${networkQuality}`);
    
    if (memoryPressure) {
      // Aggressive cleanup on memory pressure
      const entries = Array.from(memoryCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = Math.floor(entries.length * 0.3); // Remove 30% of oldest entries
      const removed = entries.slice(0, toRemove);
      
      removed.forEach(([key, entry]) => {
        entry.cleanup();
        memoryCache.delete(key);
      });
      
      console.log(`Aggressive cleanup: removed ${removed.length} entries due to memory pressure`);
    }
    
    // Clean up expired entries
    cleanupMemoryCache();
    
    // Clear preload queue on poor networks
    if (networkQuality === 'poor' || networkQuality === 'offline') {
      preloadQueue.clear();
      console.log('Cleared preload queue due to poor network conditions');
    }
    
    return await getCacheStats();
  } catch (error) {
    console.warn('Cache optimization error:', error);
    return null;
  }
};



/**
 * Smart image loader with caching and fallback
 */
export const loadImageWithCache = async (url, options = {}) => {
  if (!url) return null;
  
  try {
    // For relative URLs that might be causing issues, skip caching entirely
    if (url.startsWith('/uploads/') && !url.startsWith('http')) {
      console.warn('Skipping cache for relative URL that may cause issues:', url);
      return {
        url: url,
        metadata: null,
        cached: false,
        cacheSuccess: false,
        skipCache: true
      };
    }
    
    // Try to get from cache first, but with shorter timeout to prevent blocking
    const cachePromise = getCachedImage(url, options);
    const timeoutPromise = new Promise(resolve => 
      setTimeout(() => resolve(null), 1000) // Reduced to 1 second timeout for cache lookup
    );
    
    const cached = await Promise.race([cachePromise, timeoutPromise]);
    if (cached && cached.url) {
      console.log('Using cached image:', url);
      return cached;
    }
    
    // Cache lookup failed or timed out - proceed with original URL
    if (!cached) {
      console.log('Cache lookup timed out or failed, using original URL:', url);
    }
    
    // Only try to cache absolute URLs in background
    if (url.startsWith('http')) {
      cacheImage(url, options).catch(error => {
        console.warn('Background caching failed (non-critical):', error);
      });
    }
    
    // Always return the original URL for immediate loading
    return {
      url: url,
      metadata: null,
      cached: false,
      cacheSuccess: false
    };
  } catch (error) {
    console.warn('Cache system error (non-critical), using original URL:', error);
    
    // Always fallback to original URL - never fail
    return {
      url: url,
      metadata: null,
      cached: false,
      cacheSuccess: false,
      error: error.message
    };
  }
};